// create-friend analytics helpers.
//
// Most analytics flow through GTM (window.dataLayer.push). `lp_source` lets the
// shared GTM container (GTM-N8LQSVFS) tell createsexfriend traffic apart from the
// other landers.
//
// Mixpanel note: the ONE exception to "GTM-only" is `registration_started` in the
// paywall_popup flow, which is now sent to Mixpanel DIRECTLY from the LP (see
// utils/mixpanel.js) so users who register but never leave the popup aren't lost.
// That direct copy is keyed on the user's Mongo _id + a deterministic $insert_id
// so it counts once across the LP, backend, and platform. If the GTM container is
// ALSO configured to forward `registration_started` to Mixpanel, that anonymous
// copy would NOT dedupe against the direct one — disable it there for this event.

import { growthbook, gbReady } from '../lib/growthbookClient';
import {
  PAYWALL_NAV_CONFIG_FLAG,
  DEFAULT_NAV_CONFIG,
  resolveNavConfig,
  resolveAuthType,
} from './authType';

// Lander identifier — sent on every GTM event so the shared container can
// filter cohorts by which LP the user came from. Mirrors api.js LP_SOURCE.
const LP_SOURCE = process.env.REACT_APP_VERCEL_ENV ? 'createsexfriend' : 'swipey.ai/createsexfriend';

// `lander-content` — identifies which GrowthBook content-config variant is
// live, taken from the config's `name` field. Sent on EVERY event alongside
// `lp_source`. Defaults to 'default' until GrowthBook resolves and the provider
// calls setLanderContent().
let landerContent = 'default';

// Called by GrowthBookProvider once the GB content config resolves, passing the
// config's `name`. No-ops on empty/missing names so events keep the default.
export const setLanderContent = (name) => {
  if (name) landerContent = name;
};

// Read the current resolved lander-content — used by utils/serverGtm.js so the
// server-side GTM events carry the same attribution as the dataLayer events.
export const getLanderContent = () => landerContent;

// `createsexfriend-paywall-nav-config` — the resolved post-signup arm, sent on
// EVERY event under the GrowthBook flag's own key so the shared container reads the
// same key it already sees in `ab_testing`. Same value Everflow reports as sub7
// (utils/everflow.js) — one resolver, utils/authType.js, so GTM and Everflow can
// never disagree about which arm the user was in.
//
// Read live off the shared client instead of being pushed in from React: that keeps
// it correct for the sticky arms (resolveNavConfig) with no provider wiring. Stays
// empty until GB settles — an empty value means "not resolved yet", never a guess.
let gbSettled = false;
gbReady.then(() => { gbSettled = true }).catch(() => { gbSettled = true });

export const getPaywallNavArm = () => {
  if (!gbSettled) return '';
  try {
    return resolveAuthType(
      resolveNavConfig(
        growthbook.getFeatureValue(PAYWALL_NAV_CONFIG_FLAG, DEFAULT_NAV_CONFIG),
      ),
    );
  } catch (e) {
    return '';
  }
};

export const trackEvent = (eventName, eventParams = {}) => {
  try {
    window.dataLayer = window.dataLayer || [];

    // Every event carries `lp_source` + `lander-content` + the paywall-nav-config
    // flag. Spread caller params last so an explicit override still wins.
    const eventData = {
      event: eventName,
      lp_source: LP_SOURCE,
      'lander-content': landerContent,
      [PAYWALL_NAV_CONFIG_FLAG]: getPaywallNavArm(),
      ...eventParams,
      timestamp: new Date().toISOString(),
    };

    window.dataLayer.push(eventData);

    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Analytics Event:', eventName, eventParams);
    }

    return true;
  } catch (error) {
    console.error('Analytics tracking error:', error);
    return false;
  }
};

// === Quiz funnel (GTM-only) ===
// First screen — style picked. Replaces the old `step_1`.
export const trackStyleSelected = (style) => {
  return trackEvent('style_selected', {
    step_name: 'Style',
    style,
  });
};

export const trackStep2 = (age, ethnicity) => {
  return trackEvent('step_2', {
    step_name: 'Ethnicity',
    age,
    ethnicity,
  });
};

export const trackStep3 = (bodyType, breastSize, buttSize) => {
  return trackEvent('step_3', {
    step_name: 'Body',
    body: bodyType,
    breast: breastSize,
    booty: buttSize,
  });
};

export const trackStep4 = (hairStyle, hairColor, eyeColor) => {
  return trackEvent('step_4', {
    step_name: 'Hair',
    hair_style: hairStyle,
    hair_color: hairColor,
    eye_color: eyeColor,
  });
};

export const trackStep5 = (voice, relationship) => {
  return trackEvent('step_5', {
    step_name: 'Voice',
    voice,
    relationship,
  });
};

// Last quiz answer step — kinks. Replaces the old `step_4_5`.
export const trackStep6 = (kink) => {
  return trackEvent('step_6', {
    step_name: 'Kinks',
    kink,
  });
};

// === Registration / auth ===
// Fires when the registration form opens. GTM-only.
export const trackRegistrationModal = () => {
  return trackEvent('lander_registration_modal');
};

// Registration attempt started (email submit or Google sign-up). GTM/GA event.
// NOTE: the dataLayer/GTM event name is HYPHENATED (`registration-started`) to
// match the platform + sGTM convention (serverGtm.js trackServerRegistrationStarted).
// The Mixpanel copy is a SEPARATE event named `registration_started` (underscore),
// sent directly + identified from utils/mixpanel.js for the paywall_popup flow; the
// GTM container should NOT also forward this to Mixpanel or it double-counts on an
// anonymous id.
export const trackRegistrationStarted = (params = {}) => {
  return trackEvent('registration-started', params);
};

// Registration success. Pushes `email` into the dataLayer so the GTM container
// can pass it through to downstream tags. GTM-only.
export const trackLanderRegistrationCompleted = (source, email) => {
  return trackEvent('lander_registration_completed', {
    source,
    email,
  });
};

// Fires on successful email/password login. GTM-only.
export const trackLoginCompleted = () => {
  return trackEvent('lander_login_completed');
};

// Fires after a password reset successfully completes (new password set).
// GTM-only.
export const trackForgotPasswordCompleted = () => {
  return trackEvent('lander_forgot_pass_completed');
};

// === Paywall-popup checkout funnel (GTM) ===
// The regular (platform) paywall emits order_created / subscription_purchase to
// its GTM container. In the in-place paywall_popup the user may never reach the
// platform, so the popup emits the equivalent LP dataLayer events here so the
// funnel matches the regular paywall.

// Fires when the user opens checkout from the popup (taps Continue).
export const trackOrderCreated = (details = {}) => {
  return trackEvent('order_created', details);
};

// Subscription payment succeeded. NOTE: in paywall_popup the payment completes on
// the Upgate hosted page and the user is redirected to the platform (/lobby), so
// this normally CANNOT fire from the LP popup — it is owned by the platform.
// Exported for parity / any non-redirecting caller.
export const trackSubscriptionPurchase = (details = {}) => {
  return trackEvent('subscription_purchase', details);
};

export default {
  trackEvent,
  trackStyleSelected,
  trackStep2,
  trackStep3,
  trackStep4,
  trackStep5,
  trackStep6,
  trackRegistrationModal,
  trackRegistrationStarted,
  trackLanderRegistrationCompleted,
  trackLoginCompleted,
  trackForgotPasswordCompleted,
  trackOrderCreated,
  trackSubscriptionPurchase,
};
